import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CustomerData } from "@/types/client";
import { Table as TableType, TableAvailability, TableWeekdayAvailability, CombinedTable } from "@/types/table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, Users, Clock, MoreVertical, Edit2, Baby } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { formatCustomerName } from "@/lib/clientUtils";
import { EditReservationDialog } from "./EditReservationDialog";

interface ClientReservationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: CustomerData;
  timeSlots: { start: string; end: string; }[];
  allTables: TableType[];
  allReservations: Array<{
    id: number;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    time: string;
    reservationDate: Date;
  }>;
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  combinedTables: CombinedTable[];
  existingClients: CustomerData[];
  onReservationUpdate: (reservationId: number, updates: {
    customerName: string;
    customerEmail?: string;
    customerPhone: string;
    numberOfPeople: number;
    tableId: string;
    date: Date;
    startTime: string;
    notes?: string;
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
  }) => void;
  onClientUpdate: (oldEmail: string, updates: Partial<CustomerData>) => void;
}

export function ClientReservationsDialog({
  open,
  onOpenChange,
  client,
  timeSlots,
  allTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  combinedTables,
  existingClients,
  onReservationUpdate,
  onClientUpdate,
}: ClientReservationsDialogProps) {
  const [editingReservation, setEditingReservation] = useState<{
    id: number;
    customerId?: string;
    name: string;
    email: string;
    phone: string;
    persons: number;
    tableId: string | undefined;
    combinedTableId: string | undefined;
    time: string;
    reservationDate: Date;
    notes?: string;
    internalNotes?: string;
    status?: "planifié" | "arrivé" | "annulé" | "en_attente";
  } | null>(null);
  const getStatusVariant = (status: string) => {
    switch (status) {
      case "arrivé":
        return "default";
      case "planifié":
        return "secondary";
      case "annulé":
        return "outline";
      default:
        return "outline";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "arrivé":
        return "Arrivé";
      case "planifié":
        return "Planifié";
      case "annulé":
        return "Annulé";
      default:
        return status;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Réservations de {client.firstName} {client.lastName}</DialogTitle>
          <DialogDescription>
            {client.totalReservations} réservation(s) au total
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Heure</TableHead>
                <TableHead className="text-center">Personnes</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {client.allReservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucune réservation trouvée
                  </TableCell>
                </TableRow>
              ) : (
                client.allReservations
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((reservation, index) => (
                    <TableRow key={index} className={cn(reservation.status === "annulé" && "opacity-50 text-muted-foreground")}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {format(new Date(reservation.date), "d MMM yyyy", { locale: fr })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          {reservation.startTime}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {reservation.numberOfPeople}
                          {(reservation.numberOfChildren || 0) > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                              <Baby className="h-3 w-3" />+{reservation.numberOfChildren}
                            </span>
                          )}
                          {reservation.hasStroller && (
                            <StrollerIcon size={12} className="text-muted-foreground" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {reservation.tableId && (() => {
                          const table = allTables.find(t => t.id === reservation.tableId);
                          if (!table) {
                            return <Badge variant="outline" className="bg-red-100 text-red-700">Table supprimée</Badge>;
                          }
                          return table.name;
                        })()}
                        
                        {reservation.combinedTableId && (() => {
                          const combinedTable = combinedTables.find(ct => ct.id === reservation.combinedTableId);
                          if (!combinedTable) {
                            return <Badge variant="outline" className="bg-red-100 text-red-700">Table combinée supprimée</Badge>;
                          }
                          return combinedTable.name;
                        })()}
                        
                        {!reservation.tableId && !reservation.combinedTableId && (
                          <Badge variant="outline" className="bg-red-100 text-red-700">Aucune table</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getStatusVariant(reservation.status)}
                          className={cn(reservation.status === "annulé" && "text-muted-foreground border-muted-foreground/30 bg-muted/50")}
                        >
                          {getStatusLabel(reservation.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {reservation.notes || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingReservation({
                                  id: parseInt(reservation.id),
                                  customerId: client.id,
                                  name: formatCustomerName(client.firstName, client.lastName),
                                  email: client.email,
                                  phone: client.phone,
                                  persons: reservation.numberOfPeople,
                                  tableId: reservation.tableId,
                                  combinedTableId: reservation.combinedTableId,
                                  time: reservation.startTime,
                                  reservationDate: new Date(reservation.date),
                                  notes: reservation.notes,
                                  internalNotes: reservation.internalNotes,
                                  status: reservation.status as "planifié" | "arrivé" | "annulé" | "en_attente",
                                });
                              }}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>

      {editingReservation && (
        <EditReservationDialog
          open={!!editingReservation}
          onOpenChange={(open) => {
            if (!open) setEditingReservation(null);
          }}
          reservation={editingReservation}
          timeSlots={timeSlots}
          allTables={allTables}
          allReservations={allReservations}
          availabilities={availabilities}
          weekdayAvailabilities={weekdayAvailabilities}
          combinedTables={combinedTables}
          existingClients={existingClients}
          onReservationUpdate={onReservationUpdate}
          onClientUpdate={onClientUpdate}
        />
      )}
    </Dialog>
  );
}
